import { ModSideNav } from '@moderneinc/design-system-components';
import { Box } from '@mui/material';
import { Activity } from 'lucide-react';
import type React from 'react';
import { type FunctionComponent, useContext } from 'react';
import {
  BRANDING_LOGO_BOTTOM_GAP,
  CHANGELOG_PATH,
  CODE_SEARCH_PATH,
  DEPLOYMENT_PATH,
  DEVCENTER_PATH,
  MARKETPLACE_HOME_PATH,
  MODDY_PATH,
  NEW_BUILDER_PATH,
  ORGANIZATION_REPOSITORIES_BASE_PATH,
  RECENT_ACTIVITY_PATH
} from '../../../constants/general';
import { ConfigurationContext } from '../../../contexts/config.context';
import {
  activityUrl,
  changelogUrl,
  codeSearchUrl,
  devCenterUrl
} from '../../../helpers/link.helper';
import { useCapabilities } from '../../../hooks/use-capabilities.hooks';
import { useLlmConfiguration } from '../../../hooks/use-llm-configuration.hooks';
import {
  AiIcon,
  BrowseRecipesIcon,
  BuilderIcon,
  CodeSearchIcon,
  DeployRecipesIcon,
  DevCenterIcon,
  RecentActivityIcon,
  RepositoryIcon
} from '../../../icons/icons';
import { useSelectedOrganizationStore } from '../../../stores/organization-store';
import { useUserPreferenceStore } from '../../../stores/user-preference.store';
import { CustomerBranding } from '../left-nav-items/customer-branding.component';
import { HomeMenuItem } from '../left-nav-items/home-menu-item.component';
import { LeftNavigationMenuItem } from '../left-nav-items/left-nav-menu-item.component';

export type NavItem = {
  icon: React.ReactNode;
  label: string;
  description: string;
  href?: string | (() => string);
  shortcut?: string;
  beta?: boolean;
  /** Overrides the "Beta" label shown on beta-styled items. */
  tag?: string;
  /** Opens `href` in a new tab as an external link instead of client routing. */
  external?: boolean;
};

export const LeftNavigation: FunctionComponent = () => {
  const { changelog, codeSearch } = useCapabilities();
  const { showModdy, isLoadingConfiguration } = useLlmConfiguration();
  const { selectedOrganizationId } = useSelectedOrganizationStore();
  const { config } = useContext(ConfigurationContext);
  const collapsed = useUserPreferenceStore((s) => s.navRailCollapsed);
  const update = useUserPreferenceStore((s) => s.update);

  const devCenterNextUri = selectedOrganizationId
    ? devCenterUrl({ organizationId: selectedOrganizationId })
    : DEVCENTER_PATH;

  return (
    <ModSideNav
      collapsed={collapsed}
      onCollapsedChange={(next) => update({ navRailCollapsed: next })}
      header={<HomeMenuItem />}
      footer={
        config?.brandingIcon ? (
          <Box sx={{ width: '100%', pb: `${BRANDING_LOGO_BOTTOM_GAP}px` }}>
            <CustomerBranding config={config} collapsed={collapsed} />
          </Box>
        ) : undefined
      }
    >
      {codeSearch && (
        <LeftNavigationMenuItem
          description="Search the code of your organization's repositories"
          href={
            selectedOrganizationId
              ? codeSearchUrl(selectedOrganizationId)
              : CODE_SEARCH_PATH
          }
          icon={<CodeSearchIcon size={22} />}
          label="Code search"
          beta
          tag="Preview"
        />
      )}
      {!isLoadingConfiguration && showModdy && (
        <LeftNavigationMenuItem
          description="AI assistant"
          href={MODDY_PATH}
          icon={<AiIcon />}
          label="Moddy"
          beta
        />
      )}
      <LeftNavigationMenuItem
        description="Holistic view of your organization health and goals"
        href={devCenterNextUri}
        icon={<DevCenterIcon />}
        label="DevCenter"
      />
      <LeftNavigationMenuItem
        registerCommand
        description="View available recipes for various technologies"
        href={MARKETPLACE_HOME_PATH}
        icon={<BrowseRecipesIcon />}
        label="Marketplace"
        shortcut="M"
      />
      <LeftNavigationMenuItem
        registerCommand
        description="Compose complex custom recipes"
        href={NEW_BUILDER_PATH}
        icon={<BuilderIcon />}
        label="Builder"
        shortcut="B"
      />
      <LeftNavigationMenuItem
        registerCommand
        description="See recent activity across your selected organization"
        href={
          selectedOrganizationId
            ? activityUrl(selectedOrganizationId)
            : RECENT_ACTIVITY_PATH
        }
        icon={<RecentActivityIcon />}
        label="Activity"
        shortcut="A"
      />
      {changelog && (
        <LeftNavigationMenuItem
          description="View pull requests and commits across your organization"
          href={
            selectedOrganizationId
              ? changelogUrl(selectedOrganizationId)
              : CHANGELOG_PATH
          }
          icon={<Activity size={22} />}
          label="Changelog"
          beta
        />
      )}
      <LeftNavigationMenuItem
        registerCommand
        description="View repositories"
        href={ORGANIZATION_REPOSITORIES_BASE_PATH}
        icon={<RepositoryIcon />}
        label="Repositories"
        shortcut="R"
      />
      <LeftNavigationMenuItem
        registerCommand
        description="Deploy recipes and visualizations"
        href={DEPLOYMENT_PATH}
        icon={<DeployRecipesIcon />}
        label="Deploy"
        shortcut="D"
      />
    </ModSideNav>
  );
};
